import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Divider, Paper, Grid, Box, Stack, Tooltip, IconButton, Button
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Modal from 'components/Modal';
import useConfirm from 'core/hooks/useConfirm';
import EditIcon from '@mui/icons-material/Edit';
import EditRole from './edit';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import { getRoles, deleteRole } from 'services/roles';
import ContextSelection from 'components/contextSelection'

const RolesPage = () => {

  const gridStyle = useMemo(() => ({ minHeight: 480, width: '100%' }), []);
  const [rolesList, setRolesList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [context, setContext] = useState(null);
  const confirm = useConfirm();
  const [roleDetails, setRoleDetails] = useState({ info: null });

  const onActionBtnClick = useCallback((e, type, userData) => {
    e.preventDefault();
    if(type === 'edit'){
      setRoleDetails(userData);
      setOpenModal(true);
    }
  }, []);

  const getRolesData = useCallback((context) => {
    getRoles(null, null, `/${context}`).then(({response, error}) => {
      if(response){
        setRolesList([...(response || [])]);
      } else {
        setRolesList([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  },[enqueueSnackbar]);

  const deleteRoleById = useCallback((roleId) => {
    deleteRole(null, null, `/${roleId}`).then(({response, error}) => {
      if(error){
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      } else {
        enqueueSnackbar("Roles deleted successfully", {variant: 'success'});
        getRolesData(context);
      }
    });
  },[enqueueSnackbar, context, getRolesData]);

  const deleteRoleBtnClick = useCallback((roleId) => {
    confirm({ title: 'Are you sure you want to delete?' })
    .then(() => { 
      deleteRoleById(roleId)
    }).catch(() => {});
  }, [confirm, deleteRoleById]);

  const columns = useMemo(() => ([
    { name: 'role', header: 'Role', width: 200},
    { name: 'context', header: 'Context', width: 270},
    { name: 'actions', header: 'Actions', width: 220, render: ({data}) => {
      return (
        <Stack direction="row" spacing={1}>
          <Box>
            <Tooltip title="Edit Role" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'edit', data)}>
                <EditIcon color="warning" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title='Delete Role' arrow placement="left">
              <Button
                onClick={() => deleteRoleBtnClick(data?.roleId)} 
                size="small" 
                variant="contained"
                sx={{textTransform: 'none'}}
                color='error'
                >
                  Delete
              </Button>
            </Tooltip>
          </Box>
        </Stack>
      )
    }},
  ]),[deleteRoleBtnClick, onActionBtnClick]);

 


  useEffect(() => { 
    if(context){
      getRolesData(context);
    }
  }, [context, getRolesData]);

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
    },
    [setOpenModal],
  )

 
  const onSuccess = useCallback(
    () => {
      setOpenModal(false);
      getRolesData(context);
      setRoleDetails(null);
    },
    [setOpenModal, context, getRolesData],
  );

  const onAddRoleBtnClick = useCallback(() => {
    setRoleDetails(null)
    setOpenModal(true);
  }, []);

  const onContextSelected = useCallback((context) => {
    setContext(context);
  }, []); 

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
    {/* <CardContent> */}
      <Grid container columnSpacing={4} rowSpacing={4} >
        <Grid item xs={6} sm={9} md={10} xl={10} lg={10}>
          <Typography component="h2" variant="h6" color="secondary"  gutterBottom sx={{mb: 1}}>
            Roles
          </Typography>
        </Grid>
        <Grid item xs={6} sm={3} md={2} xl={2} lg={2}  sx={{display: 'flex'}} justifyContent="flex-end">
          <ContextSelection onContextSelected={onContextSelected} defaultContext='ADMIN' width={180} />
        </Grid>
      </Grid>
      <Divider />
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12} textAlign="right">
            <Button variant="contained" onClick={onAddRoleBtnClick}>Add Role</Button>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Box component="div" sx={{   maxHeight: 720, width: '100%' }}>
              <ReactDataGrid
                // handle={setGridRef}
                // idProperty="index"
                style={gridStyle}
                // defaultFilterValue={defaultFilterValue}
                columns={columns}
                dataSource={rolesList}
                rowHeight={null} 
                minRowHeight={50}
              />
            </Box>
          </Grid>
        </Grid>
      </Box> 
      <Modal  open={openModal} onModalClose={onModalClose} title={roleDetails ? 'Update Role' : 'Add Role'}>
        <EditRole roleDetails={roleDetails} onSuccess={onSuccess} onFailure={onModalClose} />
      </Modal>
    </Paper>
  );
}
 
export default RolesPage;
